import { format } from 'date-fns';
import { useCallback, useEffect, useState } from 'react';
import { Result, getUsers } from '../../api/api';
import { IUser } from '../../api/types';
import { departmentName } from './departmentName';

export const UsersOverview = () => {
  const [usersResult, setUsersResult] = useState<Result<IUser[]>>({
    status: 'Loading',
  });

  const loadUsers = useCallback(async () => {
    setUsersResult({ status: 'Loading' });
    const result = await getUsers();
    setUsersResult(result);
  }, []);

  useEffect(() => {
    queueMicrotask(() => {
      void loadUsers();
    });
  }, [loadUsers]);

  return (
    <div className="md:container md:mx-auto">
      <section className="table w-full p-3.5">
        {usersResult.status === 'Loading' && (
          <div role="status">Loading users...</div>
        )}

        {usersResult.status === 'Forbidden' && (
          <p className="text-red-500" role="alert">
            You do not have access to view users.
          </p>
        )}

        {isRetryableError(usersResult) && (
          <div className="flex items-center gap-3" role="alert">
            <p className="text-red-500">
              Failed to load users:{' '}
              {usersResult.errorMessages.join(', ')}
            </p>
            <button
              type="button"
              onClick={() => void loadUsers()}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              Retry
            </button>
          </div>
        )}

        {usersResult.status === 'OK' &&
          usersResult.data.length === 0 && (
            <div className="text-sm font-light" role="status">
              No users found.
            </div>
          )}

        {usersResult.status === 'OK' &&
          usersResult.data.length > 0 && (
            <table className="w-full border">
              <thead>
                <tr className="bg-gray-50 border-b">
                  <th className="p-2 border-r text-sm font-thin text-gray-500">
                    Account Id
                  </th>
                  <th className="p-2 border-r text-sm font-thin text-gray-500">
                    Name
                  </th>
                  <th className="p-2 border-r text-sm font-thin text-gray-500">
                    Department
                  </th>
                  <th className="p-2 border-r text-sm font-thin text-gray-500">
                    Created At
                  </th>
                </tr>
              </thead>
              <tbody>
                {usersResult.data.map((u) => (
                  <tr
                    key={u.accountId}
                    className="bg-gray-50 text-center"
                  >
                    <td className="p-2 border-r">{u.accountId}</td>
                    <td className="p-2 border-r">{u.name}</td>
                    <td className="p-2 border-r">
                      {departmentName(u.department)}
                    </td>
                    <td className="p-2 border-r">
                      {format(
                        new Date(u.createdAt),
                        'dd-MM-yyyy HH:mm:ss xxx',
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      </section>
    </div>
  );
};

function isRetryableError(
  result: Result<IUser[]>,
): result is Extract<
  Result<IUser[]>,
  { status: 'Error' | 'Not Found' | 'Network issue' }
> {
  return (
    result.status === 'Error' ||
    result.status === 'Not Found' ||
    result.status === 'Network issue'
  );
}
